import { Link } from "react-router-dom";
import { ArrowRight, Leaf, Star, MapPin, Check } from "lucide-react";
import { money, track } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";

export const SectionHeader = ({ eyebrow, title, intro, align = "left" }) => (
  <div className={`max-w-2xl ${align === "center" ? "mx-auto text-center" : ""}`}>
    {eyebrow && <p className="kc-eyebrow">{eyebrow}</p>}
    <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-semibold text-[var(--kc-text)]">{title}</h2>
    {intro && <p className="mt-4 text-base sm:text-lg text-[#5A606A] leading-relaxed">{intro}</p>}
  </div>
);

export const PageHero = ({ eyebrow, title, intro, children, image }) => (
  <section className="relative overflow-hidden grain border-b border-[#E5E2DA]">
    <div className="kc-container py-14 sm:py-20 grid lg:grid-cols-12 gap-10 items-center">
      <div className={image ? "lg:col-span-7" : "lg:col-span-9"}>
        <p className="kc-eyebrow rise">{eyebrow}</p>
        <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-semibold text-[var(--kc-text)] leading-[1.05] rise rise-1">{title}</h1>
        {intro && <p className="mt-6 text-lg text-[#5A606A] max-w-xl leading-relaxed rise rise-2">{intro}</p>}
        {children && <div className="mt-8 rise rise-3">{children}</div>}
      </div>
      {image && <div className="lg:col-span-5"><img src={image.src} alt={image.alt} loading="eager" className="rounded-3xl w-full aspect-[4/3] object-cover shadow-xl" /></div>}
    </div>
  </section>
);

export const BookNowButton = ({ source, label = "BOOK NOW", className = "btn-primary", testId }) => (
  <Link to="/book" data-testid={testId || `book-now-${source}`} className={className} onClick={() => track("homepage_book_clicked", { source })}>
    {label} <ArrowRight className="w-4 h-4" />
  </Link>
);

export const CtaBand = ({ source, title = "Ready for a cleaner home?", text = "Book online in minutes. Instant price. No phone tag." }) => (
  <section className="kc-section">
    <div className="kc-container">
      <div className="rounded-3xl bg-[var(--kc-blue)] text-white p-10 sm:p-16 flex flex-col md:flex-row md:items-center justify-between gap-8 relative overflow-hidden">
        <div className="relative"><h2 className="text-3xl sm:text-4xl font-semibold">{title}</h2><p className="mt-3 text-white/75 text-base sm:text-lg">{text}</p></div>
        <BookNowButton source={source} className="btn-fall bg-[#FBBF24] text-[var(--kc-text)] hover:bg-white relative" testId={`cta-band-book-now-${source}`} />
      </div>
    </div>
  </section>
);

export const FallBanner = ({ campaign, compact = false }) => {
  if (!campaign) return null;
  return (
    <div className="rounded-3xl p-8 sm:p-10 text-white relative overflow-hidden" style={{ background: "linear-gradient(135deg,#701A24 0%,#4A1018 100%)" }} data-testid="fall-special-banner">
      <Leaf className="absolute -right-6 -top-6 w-40 h-40 text-[#D9531E]/30 rotate-12" aria-hidden="true" />
      <Leaf className="absolute right-24 bottom-4 w-16 h-16 text-[#D4AF37]/25 -rotate-12 hidden sm:block" aria-hidden="true" />
      <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#D4AF37] inline-flex items-center gap-2"><Leaf className="w-4 h-4" /> {campaign.name}</p>
      <h2 className="mt-2 text-2xl sm:text-3xl font-semibold">{campaign.tagline}</h2>
      {!compact && (
        <div className="mt-6 grid sm:grid-cols-2 gap-4 max-w-2xl">
          {campaign.package_overrides.map((po) => (
            <div key={po.label} className="rounded-2xl bg-white/10 backdrop-blur-md border border-white/15 p-5" data-testid={`fall-package-${po.bedrooms}`}>
              <p className="font-semibold">{po.label}</p>
              <p className="mt-1 text-sm text-white/70 line-through">Regular {money(po.regular_price)}</p>
              <p className="text-3xl font-display font-semibold text-[#D4AF37]">{money(po.promo_price)}</p>
              <p className="text-xs text-white/70">Up to {po.included_hours} hours</p>
            </div>
          ))}
        </div>
      )}
      <div className="mt-6 flex flex-wrap gap-3">
        <Link to={`/offers/${campaign.slug}`} data-testid="fall-banner-cta" className="btn-fall">{campaign.cta_text}</Link>
        <Link to={`/offers/${campaign.slug}`} className="btn-ghost text-white hover:bg-white/10 hover:text-white h-12">View details</Link>
      </div>
    </div>
  );
};

export const ServiceAreasGrid = ({ areas, compact = false }) => (
  <div className={`grid gap-4 ${compact ? "grid-cols-2 sm:grid-cols-3 lg:grid-cols-6" : "sm:grid-cols-2 lg:grid-cols-3"}`}>
    {areas.map((a) => (
      <Link key={a.slug} to={`/service-areas/${a.slug}`} data-testid={`service-area-card-${a.slug}`}
        className="kc-card p-5 hover:border-[var(--kc-blue)] hover:-translate-y-0.5 transition-[border-color,transform] duration-200 group">
        <MapPin className="w-5 h-5 text-[var(--kc-terracotta)]" />
        <h3 className="mt-3 font-semibold text-lg group-hover:text-[var(--kc-blue)] transition-colors">{a.city}</h3>
        {!compact && <p className="mt-1 text-sm text-[#5A606A] leading-relaxed">{a.description}</p>}
      </Link>
    ))}
  </div>
);

export const FaqList = ({ faqs, testPrefix = "faq" }) => (
  <div className="divide-y divide-[#E5E2DA] kc-card px-6 sm:px-8">
    {faqs.map((f, i) => (
      <details key={f.id || i} className="group py-5" data-testid={`${testPrefix}-item-${i}`}>
        <summary className="flex cursor-pointer items-center justify-between gap-4 text-base sm:text-lg font-medium list-none [&::-webkit-details-marker]:hidden">
          {f.question}<span className="shrink-0 h-8 w-8 rounded-full border border-[#E5E2DA] grid place-items-center text-[var(--kc-blue)] group-open:rotate-45 transition-transform duration-200">+</span>
        </summary>
        <p className="mt-3 text-[#5A606A] leading-relaxed">{f.answer}</p>
      </details>
    ))}
  </div>
);

export const Testimonials = ({ items }) => (
  <div className="grid md:grid-cols-3 gap-6">
    {items.map((t, i) => (
      <figure key={t.id || i} className={`kc-card p-7 flex flex-col ${i === 1 ? "md:-translate-y-4" : ""}`} data-testid={`testimonial-${i}`}>
        <div className="flex gap-0.5 text-[var(--kc-gold)]" aria-label={`${t.rating} out of 5 stars`}>{Array.from({ length: t.rating }).map((_, j) => <Star key={j} className="w-4 h-4 fill-current" />)}</div>
        <blockquote className="mt-4 text-[var(--kc-text)] leading-relaxed flex-1">“{t.quote}”</blockquote>
        <figcaption className="mt-5 text-sm"><span className="font-semibold">{t.name}</span><span className="text-[#5A606A]"> · {t.location}{t.service ? ` · ${t.service}` : ""}</span></figcaption>
      </figure>
    ))}
  </div>
);

export const PriceTable = ({ title, packages, testId, note }) => (
  <div className="kc-card p-6 sm:p-8" data-testid={testId}>
    <h3 className="text-xl font-semibold">{title}</h3>
    {note && <p className="mt-1 text-sm text-[#5A606A]">{note}</p>}
    <ul className="mt-5 divide-y divide-[#E5E2DA]">
      {packages.map((p) => (
        <li key={p.code} className="flex items-center justify-between gap-4 py-3" data-testid={`price-row-${p.code}`}>
          <div><span className="text-[var(--kc-text)]">{p.label}</span>{p.included_hours && <span className="block text-xs text-[#5A606A]">Up to {p.included_hours} hours</span>}</div>
          <span className="text-sm text-[#5A606A] whitespace-nowrap">from <strong className="text-[var(--kc-blue)] text-lg font-display">{money(p.starting_price)}</strong></span>
        </li>
      ))}
      {packages.length === 0 && <li className="py-3 text-sm text-[#5A606A]">Pricing coming soon.</li>}
    </ul>
  </div>
);

export const IncludesList = ({ items }) => (
  <ul className="mt-4 space-y-2">
    {items.map((x) => <li key={x} className="flex items-start gap-2 text-sm text-[var(--kc-text)]"><Check className="w-4 h-4 mt-0.5 text-[var(--kc-blue)] shrink-0" />{x}</li>)}
  </ul>
);

export const useCatalogParts = () => {
  const { catalog, settings, fallCampaign } = useSettings();
  const packages = catalog?.packages || [];
  return {
    catalog, settings, fallCampaign,
    services: catalog?.service_types || [],
    standardGeneral: packages.filter((p) => p.service_code === "standard" && p.match_type === "general"),
    standardExact: packages.filter((p) => p.service_code === "standard" && p.match_type === "exact"),
    deepGeneral: packages.filter((p) => p.service_code === "deep" && p.match_type === "general"),
    addOns: catalog?.add_ons || [],
    areas: catalog?.service_areas || [],
    faqs: catalog?.faqs || [],
    testimonials: catalog?.testimonials || [],
    rules: catalog?.rules || {},
    campaigns: catalog?.campaigns || [],
  };
};
